import { ClientRoleLabelData, PassSecurityLevel } from '@app/shared/app.enums'
import { ClientRoleBooking } from './booking.model'
import { CLIENT_ID } from './client.model'
import { Pass } from './pass.model'

/** The two asset families a requirement can mint a pass for (YB-2095). Equal to the
 *  `clientRoleCategoryType` values on purpose: the key doubles as the category the target role
 *  must carry, so `asset_pass.vehicles` can only ever point at a vehicles role. */
export type AssetPassFamily = 'vehicles' | 'hme'

/**
 * One family's asset-pass policy.
 *
 * What the created pass actually needs is AREAS, and there are two ways to give it them — hence
 * both fields nullable and at least one required. Naming a `client_role` files the pass under it
 * and inherits that role's areas; naming `areas` sets them directly. With both, `areas` is an
 * OVERRIDE rather than a union: the case it exists for is a target role whose areas belong to a
 * different branch, where merging them back in is the wrong answer.
 *
 * A role-less asset pass is ordinary — `passes_extra.client_role` is nullable, and the Vehicles/HME
 * modals and the bulk import both create them routinely.
 */
export interface AssetPassConfig {
  enabled: boolean
  client_role: number | null
  areas?: number[] | null
}

/**
 * The role's configuration bag (`client_roles.extra`).
 *
 * PAPI MERGES this per namespace rather than replacing the column, so a payload carrying only
 * `asset_pass` leaves every other section alone — which matters because the portal posts the whole
 * role on every save, and a flat write would let a rename wipe a feature this screen knows nothing
 * about. Sending `{ asset_pass: null }` clears that one namespace.
 */
export interface ClientRoleExtra {
  asset_pass?: Partial<Record<AssetPassFamily, AssetPassConfig>> | null
  [namespace: string]: unknown
}

export class CreateClientRoleInput {
  name: string = ''
  client_id: CLIENT_ID = NaN
  description: string = ''
  category: 'employees' | 'authorized' | 'vehicles' | 'hme' | null = 'employees'
  event_id?: number = NaN
  areas?: number[] = []
  secured: PassSecurityLevel = PassSecurityLevel.LEVEL_1
  web_pass_sms: boolean = false
  requirements?: number[] = []
  label_data?: ClientRoleLabelData[] = []
  shift_benefits?: number = NaN
  /**
   * YB-2066 room booking. The client role IS the company for quota purposes: the allowance is a
   * credit pool shared by every pass in the role. Needs a default so `Object.keys()` picks it up
   * for the GraphQL selection set (useDefineForClassFields: false).
   */
  booking?: ClientRoleBooking | null = null
  /**
   * YB-2095 per-role configuration bag. Same `= null` requirement as `booking` above: the payload
   * and the get_client_roles selection set are both built from `Object.keys(new …Input())`, so a
   * property with no initializer is invisible to the portal in BOTH directions — it would never be
   * requested and never be sent.
   */
  extra?: ClientRoleExtra | null = null
}

export class UpdateClientRoleInput extends CreateClientRoleInput {
  id: number = NaN
}

export class ClientRole extends UpdateClientRoleInput {
  /** System-provisioned role (e.g. residential owner/tenant/guest). Read-only marker — not part of
   *  Create/UpdateClientRoleInput, so it's never sent on create/update. The settings UI blocks
   *  renaming and deleting these so the name-based residential classification stays intact. */
  is_system: boolean = false
  /** How many passes carry this role. Resolved by PAPI as a COUNT, so a screen that only shows
   *  the number (the role cards) never has to pull the member rows themselves (YB-1985). */
  members_count: number = 0
  employees: Pass[] = []
  vehicles: Pass[] = []
  hme_equipment: Pass[] = []
}

/**
 * One member row in a role drawer's list — what `get_passes_by_client_role` returns.
 *
 * Deliberately narrow: PAPI serves these from a raw projection rather than a hydrated Pass, so
 * asking for more here means widening the server query too. The pass `code` is NOT among them and
 * must not be added — it is the scan credential, and the portal has no reason to hold it.
 */
export interface ClientRoleMember {
  id: number | string
  name?: string | null
  last_name?: string | null
  /** Shown beside the name so two members called the same thing can be told apart. */
  mobile?: string | null
  /** Vehicle passes only — also one of the three fields `search` matches on. */
  vehicle_number?: string | null
  allowed?: number | null
}
